/**
 * Locale-aware formatting shared by the dashboard.
 *
 * Arabic output uses Western digits (0 to 9) by default, which is usual in
 * UAE business software. `ar-u-nu-latn` pins that. A future setting can switch
 * to Arabic-Indic digits by passing a different locale.
 */

export const intlLocale = (language: string): string => (language === 'ar' ? 'ar-u-nu-latn' : language);

/**
 * Parse a timestamp from the server. SQLite's datetime('now') gives 'YYYY-MM-DD HH:MM:SS' in
 * UTC with no zone marker, which `new Date()` would read as browser-local time. Zone-less
 * strings are treated as UTC; anything with a zone (ISO 'Z' / offset), numbers and Dates pass through.
 */
export const parseServerDate = (value: Date | number | string): Date => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value.trim())) {
    return new Date(value.trim().replace(' ', 'T') + 'Z');
  }
  return new Date(value);
};

export const formatNumber = (value: number, language: string, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(intlLocale(language), options).format(value);

export const formatDate = (value: Date | number | string, language: string, options?: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(intlLocale(language), options ?? { dateStyle: 'medium' }).format(parseServerDate(value));

export const formatDateTime = (value: Date | number | string, language: string) =>
  new Intl.DateTimeFormat(intlLocale(language), { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(parseServerDate(value));

/** "3 minutes ago" / "قبل 3 دقائق". Uses the largest unit that fits. */
export const formatRelative = (value: Date | number | string, language: string, now: number = Date.now()) => {
  const diffSeconds = Math.round((parseServerDate(value).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds) {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return rtf.format(diffSeconds, 'second');
};
